import axios from 'axios';

// Configure axios for the location service
const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const baseUrl = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;
axios.defaults.baseURL = baseUrl;
axios.defaults.withCredentials = true;

// Add authorization header
axios.interceptors.request.use(cfg => {
  const token = localStorage.getItem('token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

class LocationService {
  constructor() {
    this.watchId = null;
    this.isTracking = false;
    this.lastLocation = null;
    this.updateInterval = null;
    this.consecutiveErrors = 0;
  }

  /**
   * Start location tracking
   * @param {Object} options - Tracking options
   * @param {number} options.interval - Update interval in milliseconds (default: 30000 = 30 seconds)
   * @param {boolean} options.highAccuracy - Use high accuracy GPS (default: true)
   * @param {number} options.timeout - Location timeout in milliseconds (default: 10000)
   */
  startTracking(options = {}) {
    if (this.isTracking) {
      console.log('Location tracking already active');
      return;
    }

    const {
      interval = 30000, // 30 seconds
      highAccuracy = true,
      timeout = 10000
    } = options;

    if (!navigator.geolocation) {
      console.error('Geolocation is not supported by this browser');
      return Promise.reject(new Error('Geolocation not supported'));
    }

    this.isTracking = true;
    this.consecutiveErrors = 0; // Reset error counter
    console.log('📍 Starting location tracking...');

    // Get initial location
    this.getCurrentLocation({ highAccuracy, timeout })
      .then(location => {
        this.lastLocation = location;
        this.sendLocationToServer(location);
        this.consecutiveErrors = 0; // Reset on success
      })
      .catch(error => {
        console.error('Error getting initial location:', error);
        this.consecutiveErrors++;
      });

    // Set up periodic location updates
    this.updateInterval = setInterval(() => {
      // Stop tracking if too many consecutive errors
      if (this.consecutiveErrors >= 5) {
        console.log('📍 Stopping location tracking due to repeated errors');
        this.stopTracking();
        return;
      }

      this.getCurrentLocation({ highAccuracy, timeout })
        .then(location => {
          // Only update if location has changed significantly
          if (this.hasLocationChanged(location)) {
            this.lastLocation = location;
            this.sendLocationToServer(location);
          }
          this.consecutiveErrors = 0; // Reset on success
        })
        .catch(error => {
          console.error('Error updating location:', error);
          this.consecutiveErrors++;
        });
    }, interval);

    return Promise.resolve();
  }

  /**
   * Stop location tracking
   */
  stopTracking() {
    if (!this.isTracking) {
      console.log('Location tracking is not active');
      return;
    }

    this.isTracking = false;
    
    if (this.watchId !== null) {
      navigator.geolocation.clearWatch(this.watchId);
      this.watchId = null;
    }

    if (this.updateInterval) {
      clearInterval(this.updateInterval);
      this.updateInterval = null;
    }

    console.log('📍 Location tracking stopped');
  }

  /**
   * Get current location using browser geolocation API
   */
  getCurrentLocation(options = {}) {
    return new Promise((resolve, reject) => {
      // Check if geolocation is supported
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser. Please use a modern browser with location services.'));
        return;
      }

      const {
        highAccuracy = true,
        timeout = 10000,
        maximumAge = 60000 // 1 minute
      } = options;

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            accuracy: position.coords.accuracy,
            timestamp: new Date().toISOString()
          };

          // Get address using reverse geocoding
          this.getAddressFromCoordinates(location.latitude, location.longitude)
            .then(address => {
              resolve({
                ...location,
                address
              });
            })
            .catch(() => {
              // If reverse geocoding fails, still return location without address
              resolve(location);
            });
        },
        (error) => {
          console.error('Geolocation error:', error);
          
          let errorMessage = 'Location access failed';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please enable location permissions in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please check your GPS/network connection.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage = `Location error: ${error.message || 'Unknown error'}`;
              break;
          }
          
          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: highAccuracy,
          timeout: timeout,
          maximumAge: maximumAge
        }
      );
    });
  }

  /**
   * Get address from coordinates using Google Maps Geocoding API
   */
  async getAddressFromCoordinates(latitude, longitude) {
    try {
      if (!window.google || !window.google.maps) {
        console.warn('Google Maps API not available for reverse geocoding');
        return null;
      }

      const geocoder = new window.google.maps.Geocoder();
      const latlng = new window.google.maps.LatLng(latitude, longitude);

      return new Promise((resolve, reject) => {
        geocoder.geocode({ location: latlng }, (results, status) => {
          if (status === 'OK' && results && results[0]) {
            resolve(results[0].formatted_address);
          } else {
            console.warn('Reverse geocoding failed:', status);
            resolve(null);
          }
        });
      });
    } catch (error) {
      console.error('Error in reverse geocoding:', error);
      return null;
    }
  }

  /**
   * Send location data to server
   */
  async sendLocationToServer(location) {
    try {
      const response = await axios.post('/job-monitor/location', {
        latitude: location.latitude,
        longitude: location.longitude,
        address: location.address,
        accuracy: location.accuracy
      });

      console.log('📍 Location sent to server:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error sending location to server:', error);
      throw error;
    }
  }

  /**
   * Check if location has changed significantly
   */
  hasLocationChanged(newLocation) {
    if (!this.lastLocation) return true;

    const distance = this.calculateDistance(
      this.lastLocation.latitude,
      this.lastLocation.longitude,
      newLocation.latitude,
      newLocation.longitude
    );

    // Update if moved more than 50 meters
    return distance > 50;
  }

  /**
   * Calculate distance between two coordinates in meters
   */
  calculateDistance(lat1, lon1, lat2, lon2) {
    const R = 6371e3; // Earth's radius in meters
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lon2 - lon1) * Math.PI / 180;

    const a = Math.sin(Δφ/2) * Math.sin(Δφ/2) +
              Math.cos(φ1) * Math.cos(φ2) *
              Math.sin(Δλ/2) * Math.sin(Δλ/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));

    return R * c; // Distance in meters
  }

  /**
   * Get current tracking status
   */
  getStatus() {
    return {
      isTracking: this.isTracking,
      lastLocation: this.lastLocation
    };
  }
}

// Create singleton instance
const locationService = new LocationService();

export default locationService;
